import { Transition } from '@headlessui/react';
import React, { useState, useEffect, useRef } from 'react';
import Play from "./Play";
import { useParams, useNavigate } from "react-router";
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { db } from '../firebase';
import useAuth from '../dashboard/useAuth';
import Loading from "../common/DefaultLoading";
import AlertComponent from '../dashboard/DialogConfirm';
import AlertError from '../dashboard/PermissionDenied';
import { updateCampaignStep } from '../common/Database';
import FormComponent from './Form';
import { useUser } from '../userContext';
import { 
  ArrowTopRightOnSquareIcon,
  DevicePhoneMobileIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronUpIcon,
  CogIcon,
  ShareIcon,
  ChevronRightIcon,
  Bars3Icon,
  ArrowPathIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon
} from '@heroicons/react/24/solid';
import ModalBasic from '../components/ModalBasic';
import { encode } from 'js-base64';

export default function Editor() {
  const params = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(true); 
  const [gameData, setGameData] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState(null); 
  const [errors, setErrors] = useState({}); 
  const formRef = useRef(null); 
  const { businessProfile } = useUser();
  const [iframeKey, setIframeKey] = useState(0);
  const mobileIframeRef = useRef(null);
  const [isQuickPreviewOpen, setIsQuickPreviewOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('edit'); // 'edit' | 'preview'


  useEffect(() => {
    if (!user || !params.id) return;
    fetchCampaign();
  }, [params.id, user, navigate]);

  useEffect(() => {
    if (!businessProfile?.id) {
      return;
    }

    const companyId = businessProfile.id;

    try {
      const docRef = doc(db, "company", companyId);
      getDoc(docRef).then(doc => {
      });

      const unsubscribe = onSnapshot(
        docRef,
        {
          includeMetadataChanges: true
        },
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            if (!docSnapshot.metadata.hasPendingWrites) {
              setIframeKey(prev => prev + 1);
              
                  mobileIframeRef.current?.contentWindow?.postMessage({
      type: 'COMPANY_UPDATE',
      companyData: docSnapshot.data()
    }, '*');
            }
          }
        },
        (error) => {
          console.error("Error listening to company changes:", error);
        }
      );

      return () => {
        unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up listener:', error);
    }
  }, [businessProfile?.id, db]);

  const fetchCampaign = async () => {
    setIsLoading(true);
    try {
      if (params.id) {
        const docRef = doc(db, "campaign", params.id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const campaignData = docSnap.data();

          if (campaignData.companyId !== businessProfile.id) {
            console.error('User does not have permission to edit this campaign');
            setError("You do not have permission to edit this campaign.");
            setIsLoading(false);
            return;
          }
          setGameData(campaignData);
        } else {
          console.error('Campaign not found');
          setError("Campaign not found.");
        }
      } else {
        console.error("params.id is missing");
        setError("Campaign not found.");
      }
    } catch (error) {
      console.error('Error fetching campaign:', error);
      setError("An error occurred while fetching the campaign.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptionsUpdate = (updatedOptions) => {
    setUnsavedChanges(true);
    const newGameData = {
      ...gameData,
      options: { ...gameData.options, ...updatedOptions }
    };
    setGameData(newGameData);
    
    const previewData = {
      ...newGameData,
      id: params.id || 'preview'
    };
    
    mobileIframeRef.current?.contentWindow?.postMessage({
      type: 'PREVIEW_UPDATE',
      gameData: previewData
    }, '*');
  };

  const validateForm = () => {
    const form = formRef.current;
    if (!form.reportValidity()) {
      console.error('Validation failed, not saving');
      return false;
    }
    return true;
  };

  // Sanitize options before saving to prevent undefined values in Firestore
  const sanitizeOptions = (data) => {
    const sanitized = JSON.parse(JSON.stringify(data, (key, value) => {
      // Replace undefined with empty string for specific CTA fields
      if ((key === 'cta_text' || key === 'cta_link') && value === undefined) {
        return '';
      }
      // Remove other undefined values
      return value === undefined ? null : value;
    }));
    
    // Ensure CTA fields always exist as empty strings if undefined
    if (sanitized.options) {
      sanitized.options.cta_text = sanitized.options.cta_text || '';
      sanitized.options.cta_link = sanitized.options.cta_link || '';
    }
    
    return sanitized;
  };

  const saveOptionsToDB = async () => {
    if (!validateForm()) return;
    setUnsavedChanges(false);
    try {
      await updateCampaignStep(params.id, 3);
      const docRef = doc(db, "campaign", params.id);
      const sanitizedData = sanitizeOptions(gameData);
      await setDoc(docRef, sanitizedData, { merge: true });
      setIframeKey(prev => prev + 1);
    } catch (error) {
      console.error('Error saving options to database:', error);
    }
  };

  const closeEditor = () => {
    if (unsavedChanges) {
      setTimeout(() => {
        setShowAlert(true);
      }, 300);
    } else {
      navigate(`/studio/campaign/${params.id}`, { state: { from: '/studio/campaigns' } });
    }
  };

  const handleModalConfirm = (isOpen) => {
    if (!isOpen) {
      if (unsavedChanges) {
        setTimeout(() => {
          navigate(`/studio/campaign/${params.id}`, { state: { from: '/studio/campaigns' } });
        }, 300);
      }
    }
    setShowAlert(isOpen);
  };

  const getPreviewUrl = () => {
    const previewData = encode(JSON.stringify(gameData));
    return `/play/${params.id}?preview=${previewData}`;
  };

  const handleIframeLoad = (iframeRef) => {
    setIsPreviewLoading(false);
    const currentGameData = {
      ...gameData,
      id: params.id || 'preview'
    };
    
    iframeRef.current?.contentWindow?.postMessage({
      type: 'PREVIEW_UPDATE',
      gameData: currentGameData
    }, '*');
  };

  const refreshPreview = () => {
    setIsPreviewLoading(true);
    setIframeKey(prev => {
      return prev + 1;
    });

    setTimeout(() => {
      handleOptionsUpdate(gameData.options);
    }, 1500);
  };

  const previewUrl = getPreviewUrl();

  if (loading || isLoading) return <Loading />;

  if (error) {
    return <AlertError text={error} />;
  }

  return (
    <div className="h-[100dvh] flex flex-col md:flex-row" style={{
      background: 'linear-gradient(to bottom, #2C3E50, #1A2530)',
      fontFamily: '"Share Tech Mono", monospace'
    }}>
      {/* Desktop Layout - Hidden on Mobile */}
      <div className="hidden md:flex flex-1">
        {/* Left Panel - Form/Configuration */}
        <div className={`
          flex flex-col transition-all duration-300
          ${isSidebarCollapsed ? 'w-16' : 'flex-1'}
        `} style={{
          background: 'linear-gradient(135deg, #2C3E50, #34495E)'
        }}>
          {/* Panel Header */}
          <div className="px-4 py-4 border-b-2 flex items-center justify-between" style={{
            borderColor: 'rgba(241, 196, 15, 0.3)',
            background: 'rgba(52, 73, 94, 0.6)'
          }}>
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={closeEditor}
                className="p-2 rounded-lg transition-all duration-200 font-bold tracking-wide"
                style={{
                  background: 'rgba(52, 73, 94, 0.6)',
                  color: '#E67E22',
                  border: '1px solid rgba(230, 126, 34, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(230, 126, 34, 0.1)';
                  e.target.style.border = '1px solid rgba(230, 126, 34, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(52, 73, 94, 0.6)';
                  e.target.style.border = '1px solid rgba(230, 126, 34, 0.4)';
                }}
                title="Close editor"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-base font-bold tracking-wide truncate" style={{
                  color: '#F1C40F'
                }}>
                  {gameData.name || 'NEW GAME'}
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Save Button - moved to header */}
              <div className="h-8 flex items-center">
                {!isSidebarCollapsed && (
                  <Transition
                    show={unsavedChanges}
                    enter="transition-all duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition-all duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <button
                      type="button"
                      onClick={saveOptionsToDB}
                      className="px-4 py-2 rounded-lg text-sm font-bold tracking-wide transition-all duration-200"
                      style={{
                        background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                        color: '#FFFFFF',
                        border: '2px solid rgba(231, 76, 60, 0.8)',
                        boxShadow: '0 2px 8px rgba(231, 76, 60, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, #C0392B, #A93226)';
                        e.target.style.boxShadow = '0 4px 12px rgba(231, 76, 60, 0.5)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, #E74C3C, #C0392B)';
                        e.target.style.boxShadow = '0 2px 8px rgba(231, 76, 60, 0.3)';
                      }}
                      title="Save changes"
                    >
                      💾 SAVE CHANGES
                    </button>
                  </Transition>
                )}
              </div>
            </div>
          </div>

          {/* Panel Content */}
          <div className={`flex-1 overflow-hidden ${isSidebarCollapsed ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}>
            {!isSidebarCollapsed && (
              <div className="h-full overflow-y-auto">
                <FormComponent 
                  options={gameData.options} 
                  game={gameData} 
                  onOptionsUpdate={handleOptionsUpdate} 
                  params={params} 
                  validateForm={validateForm} 
                  errors={errors} 
                  formRef={formRef}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Desktop Preview */}
        <div className="w-96 flex flex-col" style={{
          background: 'linear-gradient(135deg, #2C3E50, #34495E)',
          borderLeft: '2px solid rgba(241, 196, 15, 0.4)'
        }}>
          {/* Preview Header */}
          <div className="px-4 py-4 border-b-2 flex items-center justify-between" style={{
            borderColor: 'rgba(241, 196, 15, 0.3)',
            background: 'rgba(52, 73, 94, 0.6)'
          }}>
            <h2 className="text-xs font-bold tracking-wide" style={{
              color: '#F1C40F'
            }}>
              MOBILE PREVIEW
            </h2>

            <div className="flex items-center gap-1">
              <div className="h-8 flex items-center gap-1">
                <button
                  onClick={refreshPreview}
                  disabled={isPreviewLoading}
                  className="p-2 rounded-lg transition-all duration-200 disabled:opacity-50 font-bold tracking-wide"
                  style={{
                    background: 'rgba(52, 73, 94, 0.6)',
                    color: '#F1C40F',
                    border: '1px solid rgba(241, 196, 15, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isPreviewLoading) {
                      e.target.style.background = 'rgba(241, 196, 15, 0.1)';
                      e.target.style.border = '1px solid rgba(241, 196, 15, 0.6)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(52, 73, 94, 0.6)';
                    e.target.style.border = '1px solid rgba(241, 196, 15, 0.4)';
                  }}
                  title="Refresh preview"
                >
                  <ArrowPathIcon className={`w-4 h-4 ${isPreviewLoading ? 'animate-spin' : ''}`} />
                </button>
                
                <a
                  href={`/play/${params.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg transition-all duration-200 font-bold tracking-wide"
                  style={{
                    background: 'rgba(52, 73, 94, 0.6)',
                    color: '#E67E22',
                    border: '1px solid rgba(230, 126, 34, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(230, 126, 34, 0.1)';
                    e.target.style.border = '1px solid rgba(230, 126, 34, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(52, 73, 94, 0.6)';
                    e.target.style.border = '1px solid rgba(230, 126, 34, 0.4)';
                  }}
                  title="Open in new tab"
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Desktop Preview Content */}
          <div className="flex-1 relative p-4" style={{background: 'rgba(44, 62, 80, 0.3)'}}>
            <div className="w-full h-full rounded-2xl overflow-hidden" style={{
              background: 'linear-gradient(135deg, #34495E, #2C3E50)',
              border: '2px solid rgba(241, 196, 15, 0.2)',
              boxShadow: '0 8px 25px rgba(241, 196, 15, 0.1)'
            }}>
              {isPreviewLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-10 rounded-2xl" style={{
                  background: 'rgba(44, 62, 80, 0.9)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div className="flex flex-col items-center gap-3 text-center">
                    <ArrowPathIcon className="w-8 h-8 animate-spin" style={{color: '#F1C40F'}} />
                    <span className="text-sm font-bold tracking-wide" style={{
                      color: '#F1C40F'
                    }}>
                      UPDATING PREVIEW...
                    </span>
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{backgroundColor: '#F1C40F'}}></div>
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{backgroundColor: '#F1C40F', animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{backgroundColor: '#F1C40F', animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </div>
              )}
              <iframe
                key={`desktop-${iframeKey}`}
                ref={mobileIframeRef}
                src={`/play/${params.id}`}
                className="w-full h-full border-0 rounded-2xl"
                title="Mobile Game Preview"
                allowFullScreen
                onLoad={() => handleIframeLoad(mobileIframeRef)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout - Visible on Mobile Only */}
      <div className="md:hidden flex flex-col flex-1">
        {/* Mobile Header */}
        <div className="px-4 py-3 border-b-2 flex items-center justify-between" style={{
          borderColor: 'rgba(241, 196, 15, 0.3)',
          background: 'rgba(52, 73, 94, 0.8)'
        }}>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              onClick={closeEditor}
              className="p-2 rounded-lg transition-all duration-200 font-bold tracking-wide flex-shrink-0"
              style={{
                background: 'rgba(52, 73, 94, 0.6)',
                color: '#E67E22',
                border: '1px solid rgba(230, 126, 34, 0.4)'
              }}
              title="Close editor"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-sm font-bold tracking-wide truncate" style={{
                color: '#F1C40F'
              }}>
                {gameData.name || 'NEW GAME'}
              </h1>
            </div>
          </div>
          
          {/* Mobile Save Button */}
          <div className="flex-shrink-0">
            <Transition
              show={unsavedChanges}
              enter="transition-all duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition-all duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <button
                type="button"
                onClick={saveOptionsToDB}
                className="px-3 py-2 rounded-lg text-xs font-bold tracking-wide transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                  color: '#FFFFFF',
                  border: '2px solid rgba(231, 76, 60, 0.8)',
                  boxShadow: '0 2px 8px rgba(231, 76, 60, 0.3)'
                }}
                title="Save changes"
              >
                💾 SAVE
              </button>
            </Transition>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b-2" style={{
          borderColor: 'rgba(241, 196, 15, 0.3)',
          background: 'rgba(44, 62, 80, 0.8)'
        }}>
          <button
            onClick={() => setActiveTab('edit')}
            className={`flex-1 px-4 py-3 text-sm font-bold tracking-wide transition-all duration-200 ${
              activeTab === 'edit' ? 'border-b-2' : ''
            }`}
            style={{
              color: activeTab === 'edit' ? '#F1C40F' : '#95A5A6',
              borderColor: activeTab === 'edit' ? '#F1C40F' : 'transparent',
              background: activeTab === 'edit' ? 'rgba(241, 196, 15, 0.1)' : 'transparent'
            }}
          >
            <CogIcon className="w-4 h-4 inline mr-2" />
            EDIT
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`flex-1 px-4 py-3 text-sm font-bold tracking-wide transition-all duration-200 ${
              activeTab === 'preview' ? 'border-b-2' : ''
            }`}
            style={{
              color: activeTab === 'preview' ? '#F1C40F' : '#95A5A6',
              borderColor: activeTab === 'preview' ? '#F1C40F' : 'transparent',
              background: activeTab === 'preview' ? 'rgba(241, 196, 15, 0.1)' : 'transparent'
            }}
          >
            <DevicePhoneMobileIcon className="w-4 h-4 inline mr-2" />
            PREVIEW
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          {/* Edit Tab Content */}
          {activeTab === 'edit' && (
            <div className="h-full overflow-y-auto" style={{
              background: 'linear-gradient(135deg, #2C3E50, #34495E)'
            }}>
              <FormComponent 
                options={gameData.options} 
                game={gameData} 
                onOptionsUpdate={handleOptionsUpdate} 
                params={params} 
                validateForm={validateForm} 
                errors={errors} 
                formRef={formRef}
              />
            </div>
          )}

          {/* Preview Tab Content */}
          {activeTab === 'preview' && (
            <div className="h-full flex flex-col" style={{
              background: 'linear-gradient(135deg, #2C3E50, #34495E)'
            }}>


              {/* Mobile Preview Content */}
              <div className="flex-1 relative p-3" style={{background: 'rgba(44, 62, 80, 0.3)'}}>
                <div className="w-full h-full rounded-xl overflow-hidden" style={{
                  background: 'linear-gradient(135deg, #34495E, #2C3E50)',
                  border: '2px solid rgba(241, 196, 15, 0.2)',
                  boxShadow: '0 8px 25px rgba(241, 196, 15, 0.1)'
                }}>
                  {isPreviewLoading && (
                    <div className="absolute inset-0 flex items-center justify-center z-10 rounded-xl" style={{
                      background: 'rgba(44, 62, 80, 0.9)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <div className="flex flex-col items-center gap-3 text-center">
                        <ArrowPathIcon className="w-6 h-6 animate-spin" style={{color: '#F1C40F'}} />
                        <span className="text-xs font-bold tracking-wide" style={{
                          color: '#F1C40F'
                        }}>
                          UPDATING...
                        </span>
                      </div>
                    </div>
                  )}
                  <iframe
                    key={`mobile-${iframeKey}`}
                    ref={mobileIframeRef}
                    src={`/play/${params.id}`}
                    className="w-full h-full border-0 rounded-xl"
                    title="Mobile Game Preview"
                    allowFullScreen
                    onLoad={() => handleIframeLoad(mobileIframeRef)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Unsaved Changes Modal */}
      <ModalBasic
        id="unsaved-changes-modal"
        title="⚠️ UNSAVED CHANGES"
        modalOpen={showAlert}
        setModalOpen={handleModalConfirm}
      >
        <div className="p-6" style={{
          background: 'linear-gradient(135deg, #2C3E50, #34495E)',
          fontFamily: '"Share Tech Mono", monospace'
        }}>
          <div className="space-y-6">
            <div className="flex items-center gap-3 p-4 rounded-lg" style={{
              background: 'rgba(241, 196, 15, 0.1)',
              border: '2px solid rgba(241, 196, 15, 0.3)'
            }}>
              <div className="w-3 h-3 rounded-full animate-pulse" style={{backgroundColor: '#F1C40F'}}></div>
              <p className="text-sm font-bold" style={{color: '#ECF0F1'}}>
                You have unsaved changes. Are you sure you want to leave?
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 text-sm font-bold tracking-wide rounded-lg transition-all duration-200"
                style={{
                  background: 'rgba(52, 73, 94, 0.6)',
                  color: '#95A5A6',
                  border: '1px solid rgba(149, 165, 166, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(149, 165, 166, 0.1)';
                  e.target.style.color = '#ECF0F1';
                  e.target.style.border = '1px solid rgba(149, 165, 166, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(52, 73, 94, 0.6)';
                  e.target.style.color = '#95A5A6';
                  e.target.style.border = '1px solid rgba(149, 165, 166, 0.4)';
                }}
                onClick={() => setShowAlert(false)}
              >
                CANCEL
              </button>
              <button
                className="px-4 py-2 text-sm font-bold tracking-wide rounded-lg transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                  color: '#FFFFFF',
                  border: '2px solid rgba(231, 76, 60, 0.6)',
                  boxShadow: '0 2px 8px rgba(231, 76, 60, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #C0392B, #A93226)';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(231, 76, 60, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #E74C3C, #C0392B)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
                onClick={() => handleModalConfirm(false)}
              >
                🚪 LEAVE PAGE
              </button>
            </div>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
}